import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import Navbar from "@/components/Navbar";
import WhatsAppFab from "@/components/WhatsAppFab";
import Footer from "@/components/Footer";
import QuickRechargeButton from "@/components/QuickRechargeButton";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { UploadSimple, PencilLine, ArrowRight, ArrowLeft, Sparkle, Buildings, CheckCircle, WarningCircle, Info } from "@phosphor-icons/react";

// Campos del payload al backend (se mantienen los 9 originales; existencias y resultado se calculan)
const BALANCE_FIELDS = [
    { k: "activos_corrientes", label: "Activos corrientes" },
    { k: "activos_no_corrientes", label: "Activos no corrientes" },
    { k: "existencias", label: "Existencias" },
    { k: "pasivos_corrientes", label: "Pasivos corrientes" },
    { k: "pasivos_no_corrientes", label: "Pasivos no corrientes" },
    { k: "patrimonio_neto", label: "Patrimonio neto" },
    { k: "ingresos", label: "Ingresos (Cifra negocio)" },
    { k: "gastos", label: "Gastos totales" },
    { k: "resultado", label: "Resultado del ejercicio" },
];

// Campos visibles en el formulario manual, agrupados en 3 columnas
const MANUAL_COLUMNS = {
    activo: {
        title: "Activo",
        subtitle: "Bienes y derechos",
        fields: [
            { k: "activos_no_corrientes", label: "Activo no corriente", hint: "Inmovilizado, inversiones a L/P" },
            { k: "activos_corrientes", label: "Activo corriente", hint: "Tesorería, clientes, existencias" },
        ],
        totalLabel: "Total Activo",
    },
    pasivo: {
        title: "Pasivo y Patrimonio",
        subtitle: "Fuentes de financiación",
        fields: [
            // Desglose del Patrimonio Neto (se suman al enviar para calcular patrimonio_neto)
            { k: "capital", label: "Capital", hint: "Capital social escriturado", group: "pn" },
            { k: "reservas", label: "Reservas", hint: "Legales, voluntarias, remanentes", group: "pn" },
            { k: "resultado_ejercicio", label: "Resultado del ejercicio", hint: "Beneficio (+) o pérdida (−) del año", group: "pn" },
            { k: "pasivos_no_corrientes", label: "Pasivo no corriente", hint: "Deuda a L/P (>1 año)" },
            { k: "pasivos_corrientes", label: "Pasivo corriente", hint: "Deuda a C/P (proveedores, préstamos <1 año)" },
        ],
        totalLabel: "Total Pasivo + Patrimonio",
    },
    pyg: {
        title: "Pérdidas y Ganancias",
        subtitle: "Cuenta de resultados",
        fields: [
            { k: "ingresos", label: "Ingresos (Cifra de negocio)", hint: "Total facturación del ejercicio" },
            { k: "gastos", label: "Gastos totales", hint: "Aprovisionamientos + personal + otros gastos" },
        ],
        totalLabel: "Resultado del ejercicio",
    },
};

const emptyBalance = {
    company_name: "",
    period: String(new Date().getFullYear() - 1),
    activos_corrientes: "",
    activos_no_corrientes: "",
    existencias: "",
    pasivos_corrientes: "",
    pasivos_no_corrientes: "",
    // Desglose Patrimonio Neto (se agregan para calcular patrimonio_neto al enviar)
    capital: "",
    reservas: "",
    resultado_ejercicio: "",
    // Campo compat: se sigue calculando/enviando patrimonio_neto = capital + reservas + resultado_ejercicio
    patrimonio_neto: "",
    ingresos: "",
    gastos: "",
    resultado: "",
};

const NEW_ANALYSIS_DRAFT_KEY = "new_analysis_draft_v1";

function loadDraft() {
    try {
        const raw = localStorage.getItem(NEW_ANALYSIS_DRAFT_KEY);
        if (!raw) return null;
        return JSON.parse(raw);
    } catch { return null; }
}

export default function NewAnalysis() {
    const nav = useNavigate();
    const { user } = useAuth();
    const [step, setStep] = useState(1); // 1: método, 2: datos, 3: procesando
    const [method, setMethod] = useState(null); // "upload" | "manual"
    const [balance, setBalance] = useState(emptyBalance);
    const [draftSavedAt, setDraftSavedAt] = useState(null);
    const [draftPrompt, setDraftPrompt] = useState(null); // {balance, saved_at} si hay borrador al montar
    const [balanceFile, setBalanceFile] = useState(null);
    const [pygFile, setPygFile] = useState(null);
    const [error, setError] = useState("");
    const [errorStatus, setErrorStatus] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [empresas, setEmpresas] = useState([]);
    const [empresaId, setEmpresaId] = useState("");
    const [saldo, setSaldo] = useState(null);
    const [showCuadreModal, setShowCuadreModal] = useState(false);
    const isAsesoria = !!user?.is_asesoria;

    useEffect(() => {
        if (!isAsesoria) return;
        (async () => {
            try {
                const [e, s] = await Promise.all([
                    api.get("/asesoria/empresas"),
                    api.get("/asesoria/saldo"),
                ]);
                setEmpresas(e.data?.items || []);
                setSaldo(s.data);
            } catch (err) {
                // No bloqueamos el flujo si falla
                console.error("Error cargando empresas:", err);
            }
        })();
    }, [isAsesoria]);

    // Detectar borrador al montar
    useEffect(() => {
        const d = loadDraft();
        if (d?.balance && (d.balance.company_name || d.balance.activos_no_corrientes || d.balance.activos_corrientes)) {
            setDraftPrompt(d);
        }
    }, []);

    // Autoguardado cada 30s en modo manual
    useEffect(() => {
        if (step !== 2 || method !== "manual") return;
        const hasContent = Object.entries(balance).some(
            ([k, v]) => !["company_name", "period"].includes(k) && v !== "" && v !== null && v !== undefined
        );
        if (!hasContent) return;
        const t = setInterval(() => {
            try {
                const now = new Date().toISOString();
                localStorage.setItem(NEW_ANALYSIS_DRAFT_KEY, JSON.stringify({ balance, saved_at: now }));
                setDraftSavedAt(now);
            } catch { /* ignore */ }
        }, 30000);
        return () => clearInterval(t);
    }, [balance, step, method]);

    function clearDraft() {
        try { localStorage.removeItem(NEW_ANALYSIS_DRAFT_KEY); } catch { /* ignore */ }
        setDraftSavedAt(null);
        setDraftPrompt(null);
    }

    function recoverDraft() {
        if (draftPrompt?.balance) {
            setBalance({ ...emptyBalance, ...draftPrompt.balance });
            setMethod("manual");
            setStep(2);
            setDraftSavedAt(draftPrompt.saved_at || null);
        }
        setDraftPrompt(null);
    }

    function setField(k, v) {
        setBalance((b) => ({ ...b, [k]: v }));
    }

    async function handleUpload() {
        if (!balanceFile && !pygFile) return;
        setError(""); setUploading(true);
        try {
            const form = new FormData();
            if (balanceFile) form.append("balance_file", balanceFile);
            if (pygFile) form.append("pyg_file", pygFile);
            const { data } = await api.post("/analyses/upload", form, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            const ex = data.extracted;
            setBalance({
                company_name: ex.company_name || "Empresa",
                period: String(ex.period || new Date().getFullYear() - 1),
                activos_corrientes: ex.activos_corrientes ?? 0,
                activos_no_corrientes: ex.activos_no_corrientes ?? 0,
                existencias: ex.existencias ?? 0,
                pasivos_corrientes: ex.pasivos_corrientes ?? 0,
                pasivos_no_corrientes: ex.pasivos_no_corrientes ?? 0,
                patrimonio_neto: ex.patrimonio_neto ?? 0,
                ingresos: ex.ingresos ?? 0,
                gastos: ex.gastos ?? 0,
                resultado: ex.resultado ?? 0,
            });
            setStep(2);
        } catch (e) {
            setError(formatApiError(e, "No se pudo procesar el archivo"));
        } finally {
            setUploading(false);
        }
    }

    async function handleSubmit(forceIgnoreCuadre = false) {
        setError("");
        if (!balance.company_name || !balance.period) {
            setError("Introduce el nombre de empresa y el periodo");
            window.scrollTo({ top: 0, behavior: "smooth" });
            return;
        }
        // Validación cuadre solo en modo manual (los uploads ya vienen desde OCR)
        if (method === "manual" && !forceIgnoreCuadre) {
            const c = computeCuadre(balance);
            if (!c.cuadrado) {
                setShowCuadreModal(true);
                return;
            }
        }
        setSubmitting(true);
        try {
            // Calcular patrimonio_neto a partir del desglose (capital + reservas + resultado_ejercicio)
            // Si el usuario no rellenó el desglose, respetamos el campo legacy patrimonio_neto.
            const capital = Number(balance.capital) || 0;
            const reservas = Number(balance.reservas) || 0;
            const rdoEjercicio = Number(balance.resultado_ejercicio) || 0;
            const patrimonioDesglose = capital + reservas + rdoEjercicio;
            const patrimonioNetoFinal = patrimonioDesglose !== 0
                ? patrimonioDesglose
                : (Number(balance.patrimonio_neto) || 0);

            const ingresos = Number(balance.ingresos) || 0;
            const gastos = Number(balance.gastos) || 0;
            const resultadoCalc = ingresos - gastos;

            const balancePayload = {
                company_name: balance.company_name,
                period: balance.period,
                activos_corrientes: Number(balance.activos_corrientes) || 0,
                activos_no_corrientes: Number(balance.activos_no_corrientes) || 0,
                existencias: Number(balance.existencias) || 0,
                pasivos_corrientes: Number(balance.pasivos_corrientes) || 0,
                pasivos_no_corrientes: Number(balance.pasivos_no_corrientes) || 0,
                patrimonio_neto: patrimonioNetoFinal,
                ingresos,
                gastos,
                resultado: method === "manual" ? resultadoCalc : (Number(balance.resultado) || 0),
                // Extras informativos (por si el backend quiere guardarlos en el futuro)
                capital,
                reservas,
                resultado_ejercicio: rdoEjercicio,
            };
            const payload = {
                empresa_id: empresaId || undefined,
                balance: balancePayload,
            };
            const { data } = await api.post("/analyses", payload);
            // Limpiar borrador tras enviar con éxito
            try { localStorage.removeItem(NEW_ANALYSIS_DRAFT_KEY); } catch { /* ignore */ }
            nav(`/analisis/${data.id}`);
        } catch (e) {
            setError(formatApiError(e, "No se pudo crear el análisis"));
            setErrorStatus(e?.response?.status || null);
            window.scrollTo({ top: 0, behavior: "smooth" });
        } finally {
            setSubmitting(false);
            setShowCuadreModal(false);
        }
    }

    return (
        <div className="min-h-screen flex flex-col">
            <Navbar />
            <main className="flex-1 max-w-5xl mx-auto w-full px-6 md:px-12 py-12">
                <div className="mb-10">
                    <div className="micro-label mb-3">— Nuevo análisis</div>
                    <h1 className="serif text-4xl md:text-5xl mb-4">Analiza un balance</h1>
                    <StepsIndicator step={step} />
                </div>

                {error && (
                    <div className="text-sm text-[var(--gf-danger)] border-l-2 border-[var(--gf-danger)] pl-3 py-2 bg-red-50 mb-6" data-testid="wizard-error">
                        {error}
                        {errorStatus === 402 && (
                            <div className="mt-3 flex flex-wrap items-center gap-3" data-testid="wizard-recharge-block">
                                <QuickRechargeButton
                                    amount={24}
                                    nextPath="/analisis/nuevo"
                                    className="btn-gold text-xs"
                                    testId="wizard-quick-recharge"
                                    beforeRedirect={() => { try { localStorage.setItem(NEW_ANALYSIS_DRAFT_KEY, JSON.stringify({ balance, saved_at: new Date().toISOString() })); } catch { /* ignore */ } }}
                                    onError={(m) => setError(m)}
                                />
                                <a href="/pagos" className="text-xs text-[var(--gf-navy)] underline underline-offset-2" data-testid="wizard-other-recharges">Otras recargas</a>
                                <span className="text-[11px] text-[var(--gf-text-muted)]">Tus cifras se guardan como borrador mientras pagas.</span>
                            </div>
                        )}
                    </div>
                )}

                {draftPrompt && (
                    <div
                        className="mb-6 p-4 border-l-4 border-l-[var(--gf-gold)] bg-[var(--gf-gold)]/10 flex items-start gap-3"
                        data-testid="draft-recover-prompt"
                    >
                        <Info size={20} className="text-[var(--gf-gold)] flex-shrink-0 mt-0.5" />
                        <div className="flex-1">
                            <div className="text-sm font-semibold text-[var(--gf-navy)]">
                                Tienes un borrador de balance sin enviar
                            </div>
                            <div className="text-xs text-[var(--gf-text-secondary)] mt-1">
                                Guardado el {draftPrompt.saved_at ? new Date(draftPrompt.saved_at).toLocaleString("es-ES") : "recientemente"}
                                {draftPrompt.balance?.company_name ? ` · ${draftPrompt.balance.company_name}` : ""}
                            </div>
                        </div>
                        <button onClick={recoverDraft} className="btn-gold text-xs" data-testid="draft-recover-btn">
                            Recuperar
                        </button>
                        <button onClick={clearDraft} className="text-xs text-[var(--gf-text-muted)] hover:text-[var(--gf-danger)] underline" data-testid="draft-discard-btn">
                            Descartar
                        </button>
                    </div>
                )}

                {step === 1 && (
                    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="grid md:grid-cols-2 gap-6" data-testid="step-1">
                        <MethodCard
                            icon={<UploadSimple size={36} weight="light" />}
                            title="Subir archivo"
                            desc="Sube el balance en PDF, Excel (XLSX) o CSV. Nuestra IA extraerá los datos."
                            badge="Recomendado"
                            selected={method === "upload"}
                            onClick={() => setMethod("upload")}
                            testId="method-upload"
                        />
                        <MethodCard
                            icon={<PencilLine size={36} weight="light" />}
                            title="Introducir manualmente"
                            desc="Rellena los campos del balance siguiendo el formato del PGC español."
                            selected={method === "manual"}
                            onClick={() => setMethod("manual")}
                            testId="method-manual"
                        />

                        {method === "upload" && (
                            <div className="md:col-span-2 gf-card mt-4">
                                <p className="text-sm text-[var(--gf-text-secondary)] mb-5">
                                    Sube uno o los dos documentos. La IA extraerá los datos y, si subes ambos, los combinará.
                                    Formatos aceptados: <span className="mono">PDF, XLSX, CSV, JPG, PNG</span>. PDFs escaneados se procesan automáticamente con OCR.
                                </p>

                                <div className="grid md:grid-cols-2 gap-5">
                                    <div>
                                        <label className="gf-label">Balance de situación <span className="text-[var(--gf-text-muted)] normal-case tracking-normal">(opcional)</span></label>
                                        <input type="file" accept=".pdf,.xlsx,.xls,.csv,.jpg,.jpeg,.png,.webp"
                                            onChange={(e) => setBalanceFile(e.target.files?.[0] || null)}
                                            className="gf-input" data-testid="upload-balance-input" />
                                        {balanceFile && (
                                            <div className="mt-2 text-xs text-[var(--gf-text-secondary)]">
                                                <span className="mono">{balanceFile.name}</span> · {(balanceFile.size / 1024).toFixed(1)} KB
                                            </div>
                                        )}
                                    </div>

                                    <div>
                                        <label className="gf-label">Cuenta de Pérdidas y Ganancias <span className="text-[var(--gf-text-muted)] normal-case tracking-normal">(opcional)</span></label>
                                        <input type="file" accept=".pdf,.xlsx,.xls,.csv,.jpg,.jpeg,.png,.webp"
                                            onChange={(e) => setPygFile(e.target.files?.[0] || null)}
                                            className="gf-input" data-testid="upload-pyg-input" />
                                        {pygFile && (
                                            <div className="mt-2 text-xs text-[var(--gf-text-secondary)]">
                                                <span className="mono">{pygFile.name}</span> · {(pygFile.size / 1024).toFixed(1)} KB
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <p className="text-xs text-[var(--gf-text-muted)] mt-4">
                                    Si tus documentos vienen en un único fichero, súbelo en cualquiera de los dos campos.
                                </p>

                                <div className="flex gap-3 mt-6">
                                    <button onClick={handleUpload} disabled={(!balanceFile && !pygFile) || uploading} className="btn-primary" data-testid="upload-submit">
                                        {uploading ? (
                                            <>
                                                <span className="elegant-loader"><span /><span /><span /></span>
                                                &nbsp; Extrayendo con IA...
                                            </>
                                        ) : (<><Sparkle size={16} /> Extraer con IA</>)}
                                    </button>
                                </div>
                            </div>
                        )}

                        {method === "manual" && (
                            <div className="md:col-span-2 mt-4">
                                <button onClick={() => { setBalance(emptyBalance); setStep(2); }} className="btn-primary" data-testid="continue-manual">
                                    Continuar <ArrowRight size={16} />
                                </button>
                            </div>
                        )}
                    </motion.div>
                )}

                {step === 2 && (
                    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="gf-card" data-testid="step-2">
                        <h2 className="serif text-2xl mb-6">Datos del balance</h2>

                        {/* Selector empresa cliente (solo asesorías) */}
                        {isAsesoria && (
                            <div className="mb-6 p-4 bg-[var(--gf-gold)]/5 border-l-4 border-l-[var(--gf-gold)]" data-testid="empresa-select-block">
                                <div className="flex flex-col md:flex-row md:items-end gap-4">
                                    <div className="flex-1">
                                        <label className="gf-label flex items-center gap-2">
                                            <Buildings size={14} weight="bold" className="text-[var(--gf-gold)]" />
                                            ¿Para qué empresa cliente? <span className="text-[var(--gf-text-muted)] normal-case tracking-normal">(consume 1 crédito)</span>
                                        </label>
                                        <select
                                            value={empresaId}
                                            onChange={(e) => setEmpresaId(e.target.value)}
                                            className="gf-input mt-1"
                                            data-testid="empresa-select"
                                        >
                                            <option value="">— Sin asociar (se cargará al nombre del balance) —</option>
                                            {empresas.map((e) => (
                                                <option key={e.id} value={e.id}>
                                                    {e.razon_social}{e.cif ? ` · ${e.cif}` : ""}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="text-right">
                                        <div className="micro-label text-[10px]">Saldo</div>
                                        <div className="serif text-xl mono">{saldo?.total_credits ?? 0}</div>
                                        <div className="text-[10px] text-[var(--gf-text-muted)]">créditos</div>
                                    </div>
                                </div>
                                {empresas.length === 0 && (
                                    <p className="text-xs text-[var(--gf-text-secondary)] mt-3 leading-relaxed">
                                        Aún no tienes empresas vinculadas. <a href="/asesoria/empresas" className="underline underline-offset-2 hover:text-[var(--gf-gold)]">Añade tu primera empresa</a> para vincular este análisis.
                                    </p>
                                )}
                            </div>
                        )}

                        <div className="grid md:grid-cols-2 gap-6 mb-8">
                            <div>
                                <label className="gf-label">Nombre de la empresa</label>
                                <input className="gf-input" value={balance.company_name}
                                    onChange={(e) => setField("company_name", e.target.value)}
                                    data-testid="field-company-name" placeholder="Acme S.L." />
                            </div>
                            <div>
                                <label className="gf-label">Periodo (año)</label>
                                <input className="gf-input mono" value={balance.period}
                                    onChange={(e) => setField("period", e.target.value)}
                                    data-testid="field-period" placeholder="2024" />
                            </div>
                        </div>

                        {/* Formulario según método */}
                        {method === "manual" ? (
                            <ManualThreeColumn balance={balance} setField={setField} draftSavedAt={draftSavedAt} />
                        ) : (
                            <div className="grid md:grid-cols-2 gap-x-6 gap-y-5">
                                {BALANCE_FIELDS.map((f) => (
                                    <div key={f.k}>
                                        <label className="gf-label">{f.label} (€)</label>
                                        <input
                                            type="number" step="0.01"
                                            className="gf-input mono"
                                            value={balance[f.k]}
                                            onChange={(e) => setField(f.k, e.target.value)}
                                            data-testid={`field-${f.k}`}
                                            placeholder="0,00"
                                        />
                                    </div>
                                ))}
                            </div>
                        )}

                        <div className="flex justify-between mt-10 pt-6 border-t border-[var(--gf-border)]">
                            <button onClick={() => setStep(1)} className="btn-outline" data-testid="back-step-1">
                                <ArrowLeft size={16} /> Atrás
                            </button>
                            <button onClick={() => handleSubmit(false)} disabled={submitting} className="btn-gold" data-testid="submit-analysis">
                                {submitting ? "Calculando..." : "Generar informe"} <ArrowRight size={16} />
                            </button>
                        </div>
                    </motion.div>
                )}

                {/* Modal aviso descuadre */}
                {showCuadreModal && (
                    <CuadreModal
                        balance={balance}
                        onCancel={() => setShowCuadreModal(false)}
                        onConfirm={() => handleSubmit(true)}
                        submitting={submitting}
                    />
                )}
            </main>
            <Footer />
            <WhatsAppFab />
        </div>
    );
}

function MethodCard({ icon, title, desc, badge, selected, onClick, testId }) {
    return (
        <button onClick={onClick} className={`text-left gf-card ${selected ? "border-[var(--gf-gold)] border-t-2" : ""}`}
            style={{ borderColor: selected ? "var(--gf-gold)" : undefined }}
            data-testid={testId}>
            <div className="flex items-center justify-between mb-4">
                <div className="text-[var(--gf-navy)]">{icon}</div>
                {badge && <span className="text-[10px] tracking-widest uppercase bg-[var(--gf-gold)] text-white px-2 py-1">{badge}</span>}
            </div>
            <h3 className="serif text-2xl mb-2">{title}</h3>
            <p className="text-sm text-[var(--gf-text-secondary)]">{desc}</p>
        </button>
    );
}

function StepsIndicator({ step }) {
    const steps = ["Método", "Datos", "Informe"];
    return (
        <div className="flex items-center gap-3 mt-6">
            {steps.map((label, i) => {
                const n = i + 1;
                const active = n <= step;
                return (
                    <React.Fragment key={label}>
                        <div className="flex items-center gap-3">
                            <div className={`w-8 h-8 flex items-center justify-center text-sm serif ${active ? "bg-[var(--gf-navy)] text-white" : "bg-[var(--gf-subtle)] text-[var(--gf-text-muted)]"}`}>
                                {n}
                            </div>
                            <span className={`micro-label ${active ? "text-[var(--gf-navy)]" : "text-[var(--gf-text-muted)]"}`}>{label}</span>
                        </div>
                        {i < steps.length - 1 && <div className="flex-1 h-px bg-[var(--gf-border)] min-w-[40px]" />}
                    </React.Fragment>
                );
            })}
        </div>
    );
}


// ---------- Helpers de cuadre y formato ----------

function computeCuadre(balance) {
    const activoTotal =
        (Number(balance.activos_no_corrientes) || 0) +
        (Number(balance.activos_corrientes) || 0);
    // Patrimonio Neto ahora es la suma de sus 3 componentes (si están rellenos);
    // se sigue soportando el campo `patrimonio_neto` legacy (upload OCR).
    const capital = Number(balance.capital) || 0;
    const reservas = Number(balance.reservas) || 0;
    const rdo = Number(balance.resultado_ejercicio) || 0;
    const patrimonioDesglose = capital + reservas + rdo;
    const patrimonioLegacy = Number(balance.patrimonio_neto) || 0;
    const patrimonio = patrimonioDesglose !== 0 ? patrimonioDesglose : patrimonioLegacy;
    const pasivoTotal =
        patrimonio +
        (Number(balance.pasivos_no_corrientes) || 0) +
        (Number(balance.pasivos_corrientes) || 0);
    const resultado =
        (Number(balance.ingresos) || 0) - (Number(balance.gastos) || 0);
    const diferencia = activoTotal - pasivoTotal;
    // Tolerancia: 1€ para redondeos
    const cuadrado = Math.abs(diferencia) <= 1;
    return { activoTotal, pasivoTotal, resultado, diferencia, cuadrado, patrimonio };
}

function formatNumEUR(n) {
    if (n === "" || n === null || n === undefined || Number.isNaN(Number(n))) return "—";
    return new Intl.NumberFormat("es-ES", {
        style: "currency",
        currency: "EUR",
        maximumFractionDigits: 2,
    }).format(Number(n));
}

// ---------- Ratios en vivo ----------

function LiveRatios({ balance }) {
    const ac = Number(balance.activos_corrientes) || 0;
    const anc = Number(balance.activos_no_corrientes) || 0;
    const pc = Number(balance.pasivos_corrientes) || 0;
    const pnc = Number(balance.pasivos_no_corrientes) || 0;
    // Patrimonio Neto = capital + reservas + resultado (desglosado) o legacy field
    const capital = Number(balance.capital) || 0;
    const reservas = Number(balance.reservas) || 0;
    const rdoDeclarado = Number(balance.resultado_ejercicio) || 0;
    const desglose = capital + reservas + rdoDeclarado;
    const fp = desglose !== 0 ? desglose : (Number(balance.patrimonio_neto) || 0);
    const ing = Number(balance.ingresos) || 0;
    const gas = Number(balance.gastos) || 0;
    const resultado = ing - gas;
    const totalPP = fp + pnc + pc;
    const totalActivo = ac + anc;

    // Solo mostrar cuando ya haya datos mínimos
    const hasEnoughData = totalActivo > 0 || totalPP > 0 || ing > 0;
    if (!hasEnoughData) return null;

    const ratios = [
        {
            k: "liquidez",
            label: "Liquidez corriente",
            value: pc > 0 ? ac / pc : null,
            format: (v) => v.toFixed(2),
            optimal: "≥ 1,5",
            health: (v) => (v === null ? "n/a" : v >= 1.5 ? "good" : v >= 1 ? "warn" : "bad"),
            hint: "Activo Corriente / Pasivo Corriente",
        },
        {
            k: "endeudamiento",
            label: "Endeudamiento",
            value: totalPP > 0 ? ((pnc + pc) / totalPP) * 100 : null,
            format: (v) => `${v.toFixed(1)}%`,
            optimal: "≤ 60%",
            health: (v) => (v === null ? "n/a" : v <= 60 ? "good" : v <= 75 ? "warn" : "bad"),
            hint: "(Pasivo NC + Pasivo C) / Total",
        },
        {
            k: "autonomia",
            label: "Autonomía financiera",
            value: totalPP > 0 ? (fp / totalPP) * 100 : null,
            format: (v) => `${v.toFixed(1)}%`,
            optimal: "≥ 40%",
            health: (v) => (v === null ? "n/a" : v >= 40 ? "good" : v >= 25 ? "warn" : "bad"),
            hint: "Fondos Propios / Total",
        },
        {
            k: "margen",
            label: "Margen neto",
            value: ing > 0 ? (resultado / ing) * 100 : null,
            format: (v) => `${v.toFixed(1)}%`,
            optimal: "≥ 5%",
            health: (v) => (v === null ? "n/a" : v >= 5 ? "good" : v >= 0 ? "warn" : "bad"),
            hint: "Resultado / Ingresos",
        },
    ];

    const healthClasses = {
        good: "bg-emerald-50 border-l-emerald-500 text-emerald-800",
        warn: "bg-amber-50 border-l-amber-500 text-amber-800",
        bad: "bg-red-50 border-l-red-500 text-red-800",
        "n/a": "bg-[var(--gf-subtle)] border-l-[var(--gf-border)] text-[var(--gf-text-muted)]",
    };
    const healthDot = {
        good: "bg-emerald-500",
        warn: "bg-amber-500",
        bad: "bg-red-500",
        "n/a": "bg-[var(--gf-border)]",
    };

    return (
        <div data-testid="live-ratios">
            <div className="micro-label mb-2">— Ratios en vivo</div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                {ratios.map((r) => {
                    const h = r.health(r.value);
                    return (
                        <div
                            key={r.k}
                            className={`border-l-4 px-3 py-2 ${healthClasses[h]}`}
                            data-testid={`live-ratio-${r.k}`}
                            title={r.hint}
                        >
                            <div className="flex items-center justify-between gap-2">
                                <span className="text-[11px] uppercase tracking-widest font-medium">{r.label}</span>
                                <span className={`inline-block w-2 h-2 rounded-full ${healthDot[h]}`} />
                            </div>
                            <div className="serif text-xl mt-1 mono">
                                {r.value === null ? "—" : r.format(r.value)}
                            </div>
                            <div className="text-[10px] text-[var(--gf-text-muted)] mt-0.5">
                                Óptimo {r.optimal}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

// ---------- Formulario manual en 3 columnas ----------

function ManualThreeColumn({ balance, setField, draftSavedAt }) {
    const { activoTotal, pasivoTotal, resultado, diferencia, cuadrado } = computeCuadre(balance);

    return (
        <div className="space-y-6" data-testid="manual-three-column">
            {/* Banner de cuadre */}
            <div
                className={`flex items-start gap-3 p-4 border-l-4 ${
                    cuadrado
                        ? "border-l-emerald-500 bg-emerald-50"
                        : "border-l-red-500 bg-red-50"
                }`}
                data-testid="cuadre-banner"
            >
                {cuadrado ? (
                    <CheckCircle size={22} weight="fill" className="text-emerald-600 flex-shrink-0 mt-0.5" />
                ) : (
                    <WarningCircle size={22} weight="fill" className="text-red-600 flex-shrink-0 mt-0.5" />
                )}
                <div className="flex-1">
                    <div className={`font-semibold text-sm ${cuadrado ? "text-emerald-800" : "text-red-800"}`}>
                        {cuadrado
                            ? "Balance cuadrado"
                            : `Balance descuadrado · Diferencia ${formatNumEUR(diferencia)}`}
                    </div>
                    <div className="text-xs text-[var(--gf-text-secondary)] mt-1 mono">
                        Activo {formatNumEUR(activoTotal)} · Pasivo + Patrimonio {formatNumEUR(pasivoTotal)}
                    </div>
                    {!cuadrado && (
                        <div className="text-xs text-red-700 mt-1">
                            El total del Activo debe coincidir con Fondos Propios + Pasivo No Corriente + Pasivo Corriente.
                        </div>
                    )}
                </div>
                {draftSavedAt && (
                    <div className="text-[10px] text-[var(--gf-text-muted)] flex-shrink-0 text-right leading-tight">
                        Borrador guardado<br />
                        <span className="mono">{new Date(draftSavedAt).toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" })}</span>
                    </div>
                )}
            </div>

            {/* Ratios en vivo */}
            <LiveRatios balance={balance} />

            {/* 3 columnas */}
            <div className="grid md:grid-cols-3 gap-5">
                <ManualColumn
                    columnKey="activo"
                    cfg={MANUAL_COLUMNS.activo}
                    balance={balance}
                    setField={setField}
                    total={activoTotal}
                    accent="navy"
                />
                <ManualColumn
                    columnKey="pasivo"
                    cfg={MANUAL_COLUMNS.pasivo}
                    balance={balance}
                    setField={setField}
                    total={pasivoTotal}
                    accent="navy"
                />
                <ManualColumn
                    columnKey="pyg"
                    cfg={MANUAL_COLUMNS.pyg}
                    balance={balance}
                    setField={setField}
                    total={resultado}
                    accent="gold"
                    totalHint="Ingresos − Gastos (ya incluido en Fondos Propios)"
                    allowNegative
                />
            </div>

            <p className="text-xs text-[var(--gf-text-muted)] flex items-start gap-2">
                <Info size={14} className="flex-shrink-0 mt-0.5" />
                Introduce importes en euros. Los totales se calculan automáticamente. Recuerda que el
                Resultado del ejercicio ya debe estar sumado (o restado) dentro de los Fondos propios.
            </p>
        </div>
    );
}

function ManualColumn({ columnKey, cfg, balance, setField, total, accent, totalHint, allowNegative }) {
    const isGold = accent === "gold";
    // Suma del sub-grupo `pn` (Patrimonio Neto) para mostrar como subtotal
    const pnFields = cfg.fields.filter((f) => f.group === "pn");
    const pnSum = pnFields.reduce((acc, f) => acc + (Number(balance[f.k]) || 0), 0);
    return (
        <div
            className="border border-[var(--gf-border)] bg-white p-5"
            data-testid={`manual-col-${columnKey}`}
        >
            <div className="micro-label mb-1">— {cfg.subtitle}</div>
            <h3 className={`serif text-xl mb-5 ${isGold ? "text-[var(--gf-gold)]" : "text-[var(--gf-navy)]"}`}>
                {cfg.title}
            </h3>
            <div className="space-y-4">
                {cfg.fields.map((f, idx) => {
                    const isPn = f.group === "pn";
                    const isFirstPn = isPn && idx === cfg.fields.findIndex((x) => x.group === "pn");
                    const isLastPn = isPn && idx === cfg.fields.map((x) => x.group).lastIndexOf("pn");
                    return (
                        <React.Fragment key={f.k}>
                            {isFirstPn && (
                                <div className="pt-2 border-t border-dashed border-[var(--gf-border)]" data-testid="pn-header">
                                    <div className="micro-label mb-1 text-[var(--gf-gold)]">— Patrimonio Neto</div>
                                    <div className="text-[10px] text-[var(--gf-text-muted)] mb-3">Desglosa capital, reservas y resultado del ejercicio</div>
                                </div>
                            )}
                            <div>
                                <label className="gf-label" title={f.hint}>{f.label} (€)</label>
                                <input
                                    type="number"
                                    step="0.01"
                                    className="gf-input mono"
                                    value={balance[f.k]}
                                    onChange={(e) => setField(f.k, e.target.value)}
                                    data-testid={`field-${f.k}`}
                                    placeholder="0,00"
                                />
                                {f.hint && (
                                    <div className="text-[11px] text-[var(--gf-text-muted)] mt-1">{f.hint}</div>
                                )}
                            </div>
                            {isLastPn && (
                                <div className="flex items-baseline justify-between px-3 py-2 bg-[var(--gf-subtle)] border-l-2 border-l-[var(--gf-gold)]" data-testid="pn-subtotal">
                                    <span className="text-[11px] uppercase tracking-widest text-[var(--gf-text-muted)]">= Fondos propios</span>
                                    <span className="mono font-semibold text-[var(--gf-navy)]">{formatNumEUR(pnSum)}</span>
                                </div>
                            )}
                        </React.Fragment>
                    );
                })}
            </div>
            <div
                className={`mt-5 pt-4 border-t-2 ${
                    isGold ? "border-t-[var(--gf-gold)]" : "border-t-[var(--gf-navy)]"
                }`}
            >
                <div className="micro-label mb-1">= {cfg.totalLabel}</div>
                <div
                    className={`serif text-2xl mono ${
                        allowNegative && total < 0 ? "text-red-600" : isGold ? "text-[var(--gf-gold)]" : "text-[var(--gf-navy)]"
                    }`}
                    data-testid={`total-${columnKey}`}
                >
                    {formatNumEUR(total)}
                </div>
                {totalHint && (
                    <div className="text-[11px] text-[var(--gf-text-muted)] mt-1">{totalHint}</div>
                )}
            </div>
        </div>
    );
}

// ---------- Modal de aviso de descuadre ----------

function CuadreModal({ balance, onCancel, onConfirm, submitting }) {
    const { activoTotal, pasivoTotal, diferencia } = computeCuadre(balance);
    return (
        <div
            className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
            data-testid="cuadre-modal"
        >
            <div className="bg-white max-w-lg w-full p-8 shadow-xl">
                <div className="flex items-start gap-3 mb-4">
                    <WarningCircle size={28} weight="fill" className="text-red-600 flex-shrink-0" />
                    <div>
                        <h3 className="serif text-2xl text-[var(--gf-navy)]">El balance no cuadra</h3>
                        <p className="text-sm text-[var(--gf-text-secondary)] mt-1">
                            La ecuación contable básica no se cumple.
                        </p>
                    </div>
                </div>
                <div className="bg-[var(--gf-subtle)] p-4 mb-5 text-sm space-y-1">
                    <div className="flex justify-between">
                        <span>Total Activo</span>
                        <span className="mono font-semibold">{formatNumEUR(activoTotal)}</span>
                    </div>
                    <div className="flex justify-between">
                        <span>Total Pasivo + Patrimonio</span>
                        <span className="mono font-semibold">{formatNumEUR(pasivoTotal)}</span>
                    </div>
                    <div className="flex justify-between pt-2 mt-2 border-t border-[var(--gf-border)] text-red-700">
                        <span className="font-semibold">Diferencia</span>
                        <span className="mono font-bold">{formatNumEUR(diferencia)}</span>
                    </div>
                </div>
                <p className="text-sm text-[var(--gf-text-secondary)] mb-6 leading-relaxed">
                    Los ratios y el informe pueden salir distorsionados. Revisa las cifras antes de continuar
                    o pulsa <strong>«Generar de todos modos»</strong> si sabes lo que haces.
                </p>
                <div className="flex justify-end gap-3">
                    <button onClick={onCancel} className="btn-outline" data-testid="cuadre-modal-cancel">
                        Revisar cifras
                    </button>
                    <button
                        onClick={onConfirm}
                        disabled={submitting}
                        className="btn-gold"
                        data-testid="cuadre-modal-confirm"
                    >
                        {submitting ? "Calculando..." : "Generar de todos modos"}
                    </button>
                </div>
            </div>
        </div>
    );
}
